import {setDifference} from '@benchling/js-utils';
import {mapObject} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {pluralize} from 'BenchlingUtil';
import {ActionOrigin, MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  NormalizedConstruct,
  NormalizedFragment,
  PRIMERS_FRAGMENT_PRODUCTION_METHODS,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {ValidationStatus} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import BulkAssemblyCardSection from 'molbio/bulk-assembly/overview-tab/BulkAssemblyCardSection';
import {withEditConfirmation} from 'molbio/bulk-assembly/overview-tab/BulkAssemblyEditConfirmationModal';
import BulkAssemblyHeader from 'molbio/bulk-assembly/overview-tab/BulkAssemblyHeader';
import ConstructModal from 'molbio/bulk-assembly/polymer-map-modals/ConstructModal';
import FragmentModal from 'molbio/bulk-assembly/polymer-map-modals/FragmentModal';
import BulkAssemblyConstructsTable from 'molbio/bulk-assembly/tables/BulkAssemblyConstructsTable';
import BulkAssemblyFragmentsTable from 'molbio/bulk-assembly/tables/BulkAssemblyFragmentsTable';
import {useAssemblyCoordinator} from 'molbio/bulk-assembly/useAssemblyCoordinator';
import {StructuredTableViewPanel} from 'notebook/tables/table-views/TableViewPanel';
import BenchlingProfiler, {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  CONSTRUCTS_TABLE_RENDER_FINISHED_EVENT,
  CONSTRUCTS_TABLE_RENDER_STARTED_EVENT,
  FRAGMENTS_TABLE_RENDER_FINISHED_EVENT,
  FRAGMENTS_TABLE_RENDER_STARTED_EVENT,
} from 'performance/processMolbioEvents';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

export interface BulkAssemblyOverviewTabProps {
  assemblyId: string;
  onSetName: (name: string) => Promise<void>;
  onSetFolder: (folderId: string) => void;
}

function getFilteredOutCids(
  normalizedRows: Array<NormalizedFragment | NormalizedConstruct>,
  tableRows: Array<{cid: string}>
): Set<string> {
  // When filtering, the table is operating on a subset of the data, which means that upon update it may appear that
  // some items have been deleted.
  return setDifference(new Set(normalizedRows.map(({cid}) => cid)), new Set(tableRows.map(({cid}) => cid)));
}

export default function BulkAssemblyOverviewTab({
  assemblyId,
  onSetName,
  onSetFolder,
}: BulkAssemblyOverviewTabProps): JSX.Element {
  const assemblyCoordinator = useAssemblyCoordinator(assemblyId);
  if (assemblyCoordinator == null) {
    return <CenteredHorizontalThrobber />;
  }
  const isReadOnly = assemblyCoordinator.isReadOnly();

  logProfilerTimingEvent(FRAGMENTS_TABLE_RENDER_STARTED_EVENT);
  logProfilerTimingEvent(CONSTRUCTS_TABLE_RENDER_STARTED_EVENT);

  const tableFragments = assemblyCoordinator.getTableFragments();
  const tableConstructs = assemblyCoordinator.getTableConstructs();

  return (
    <div
      {...getTestProps({component: 'BulkAssemblyOverviewTab', element: 'root'})}
      className={css(styles.pageWrapper)}
    >
      <StructuredTableViewPanel>
        <div className={css(styles.scrollingContainer)}>
          <BulkAssemblyHeader
            assemblyCoordinator={assemblyCoordinator}
            onSetName={onSetName}
            onSetFolder={onSetFolder}
          />
          <BulkAssemblyCardSection assemblyCoordinator={assemblyCoordinator} />
          <div className={css(styles.fragmentsTableWrapper)}>
            <BenchlingProfiler
              {...FRAGMENTS_TABLE_RENDER_FINISHED_EVENT}
              extra={{numFragments: tableFragments.length}}
            >
              <BulkAssemblyFragmentsTable
                assemblyId={assemblyId}
                bulkAssemblyMethodData={assemblyCoordinator.assemblySelector.getMethod()}
                isFilterDisabledByStatus={
                  mapObject(
                    assemblyCoordinator.getFilteredFragmentCidsByStatus(),
                    (fragmentCids) => fragmentCids.size === 0
                  ) as {
                    [key in ValidationStatus]: boolean;
                  }
                }
                fragments={tableFragments}
                bins={assemblyCoordinator.assemblySelector.getFragmentBins()}
                isReadOnly={isReadOnly}
                canUsePrimers={PRIMERS_FRAGMENT_PRODUCTION_METHODS.includes(
                  assemblyCoordinator.getFragmentProductionMethod()
                )}
                onChangeFragments={(fragments) => {
                  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_FRAGMENTS_TABLE, {
                    actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
                    assemblyId: assemblyCoordinator.assemblyId,
                  });
                  const filteredOutFragmentCids = getFilteredOutCids(
                    assemblyCoordinator.state.fragments,
                    tableFragments
                  );
                  assemblyCoordinator.handleTableFragmentsUpdate(fragments, filteredOutFragmentCids);
                }}
                onReorderFragments={(reorderedFragmentCids) => {
                  const newFragments = reorderedFragmentCids.map(
                    (fragmentCid) => assemblyCoordinator.fragmentsByCid[fragmentCid]
                  );
                  assemblyCoordinator.replaceFragments(
                    newFragments,
                    getFilteredOutCids(assemblyCoordinator.state.fragments, tableFragments)
                  );
                }}
                onViewFragments={async (fragmentCids: Array<string>) => {
                  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_BULK_ASSEMBLY_EDIT_FRAGMENT_MODAL, {
                    actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
                    assemblyId: assemblyCoordinator.assemblyId,
                  });
                  const normalizedFragments = fragmentCids.map(
                    (fragmentCid) => assemblyCoordinator.fragmentsByCid[fragmentCid]
                  );
                  // TODO(BNCH-99599): consider deferring data loading until the fragment is actually viewed in the modal for perf
                  const fragmentsData = await withLoadingScreen(
                    Promise.all(
                      normalizedFragments.map((normalizedFragment) =>
                        assemblyCoordinator.getFragmentModalFragments(normalizedFragment)
                      )
                    )
                  );
                  const maybeUpdatedFragments = await runModal(FragmentModal, {
                    assemblyId,
                    bulkAssemblyMethod: assemblyCoordinator.assemblySelector.getMethod(),
                    canSpecifyEndpoints: assemblyCoordinator.canSpecifyEndpoints(),
                    initialFragmentsData: fragmentsData,
                    isReadOnly,
                    constructTopology: assemblyCoordinator.assemblySelector.getSequenceTopology(),
                    numConstructFragments: assemblyCoordinator.assemblySelector.getNumFragmentBins(),
                  });
                  if (maybeUpdatedFragments != null) {
                    analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_FRAGMENT, {
                      actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
                      assemblyId: assemblyCoordinator.assemblyId,
                    });
                    assemblyCoordinator.updateFragments(maybeUpdatedFragments);
                  }
                }}
                onDeleteFiltered={async () => {
                  await withEditConfirmation({
                    onConfirm: () => {
                      assemblyCoordinator.deleteFilteredFragments();
                      assemblyCoordinator.setFragmentStatusFilter(null);
                    },
                    title: 'Delete fragments?',
                    warnings: [
                      `You're about to delete ${tableFragments.length} ${pluralize(tableFragments.length, 'fragment')} from the assembly. This action cannot be undone.`,
                    ],
                  });
                }}
                onDeleteAll={async () => {
                  await withEditConfirmation({
                    onConfirm: () => {
                      assemblyCoordinator.replaceFragments([], new Set());
                      assemblyCoordinator.setFragmentStatusFilter(null);
                    },
                    title: 'Delete fragments?',
                    warnings: [
                      `You're about to delete ${assemblyCoordinator.state.fragments.length} ${pluralize(assemblyCoordinator.state.fragments.length, 'fragment')} from the assembly. This action cannot be undone.`,
                    ],
                  });
                }}
                onDeleteRange={async (startIndex: number, endIndex: number) => {
                  const fragmentCidsToDelete = new Set(
                    tableFragments.slice(startIndex, endIndex + 1).map(({cid}) => cid)
                  );
                  await withEditConfirmation({
                    onConfirm: () => {
                      assemblyCoordinator.replaceFragments(
                        // deletion logic already accounts for filtered out fragments
                        assemblyCoordinator.state.fragments.filter(({cid}) => !fragmentCidsToDelete.has(cid)),
                        new Set()
                      );
                    },
                    title: 'Delete fragments',
                    warnings: [
                      `You're about to delete ${fragmentCidsToDelete.size} ${pluralize(fragmentCidsToDelete.size, 'fragment')} from the assembly. This action cannot be undone.`,
                    ],
                  });
                }}
                statusFilter={assemblyCoordinator.getFragmentStatusFilter()}
                onSetStatusFilter={(newStatusFilter) => {
                  assemblyCoordinator.setFragmentStatusFilter(newStatusFilter);
                }}
                canSpecifyEndpoints={assemblyCoordinator.canSpecifyEndpoints()}
                supportedPolymerTypes={assemblyCoordinator.supportedPolymerSubtypes()}
                icon={assemblyCoordinator.getFragmentIcon()}
              />
            </BenchlingProfiler>
          </div>
          <div className={css(styles.constructsTableWrapper)}>
            <BenchlingProfiler
              {...CONSTRUCTS_TABLE_RENDER_FINISHED_EVENT}
              extra={{numConstructs: tableConstructs.length}}
            >
              <BulkAssemblyConstructsTable
                assemblyId={assemblyId}
                bulkAssemblyMethodData={assemblyCoordinator.assemblySelector.getMethod()}
                bins={assemblyCoordinator.assemblySelector.getFragmentBins()}
                constructs={tableConstructs}
                fragmentInfosByBinCid={assemblyCoordinator.specifiedFragmentInfosByBinCid}
                isFilterDisabledByStatus={
                  mapObject(
                    assemblyCoordinator.getFilteredConstructCidsByStatus(),
                    (constructCids) => constructCids.size === 0
                  ) as {[key in ValidationStatus]: boolean}
                }
                isReadOnly={isReadOnly}
                onChangeConstructs={(constructs) => {
                  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_CONSTRUCTS_TABLE, {
                    actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
                    assemblyId: assemblyCoordinator.assemblyId,
                  });
                  assemblyCoordinator.handleTableConstructsUpdate(
                    constructs,
                    getFilteredOutCids(assemblyCoordinator.state.constructs, tableConstructs)
                  );
                }}
                onAutopopulate={() => {
                  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.CLICKED_BULK_ASSEMBLY_AUTOPOPULATE_BUTTON, {
                    actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
                    assemblyId: assemblyCoordinator.assemblyId,
                  });
                  assemblyCoordinator.handleAutopopulateConstructs();
                }}
                onDeleteAll={async () => {
                  analytics.trackEvent(
                    MOLBIO_ANALYTICS_ACTIONS.CLICKED_BULK_ASSEMBLY_CLEAR_CONSTRUCTS_BUTTON,
                    {
                      actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
                      assemblyId: assemblyCoordinator.assemblyId,
                    }
                  );
                  await withEditConfirmation({
                    confirmationText: 'Delete',
                    onConfirm: () => {
                      assemblyCoordinator.replaceConstructs([]);
                      assemblyCoordinator.setConstructStatusFilter(null);
                    },
                    title: 'Delete constructs?',
                    warnings: [
                      `You're about to delete ${assemblyCoordinator.state.constructs.length} ${pluralize(assemblyCoordinator.state.constructs.length, 'construct')} from the assembly. This action cannot be undone.`,
                    ],
                  });
                }}
                onDeleteFiltered={async () => {
                  await withEditConfirmation({
                    confirmationText: 'Delete',
                    onConfirm: () => {
                      assemblyCoordinator.deleteFilteredConstructs();
                      assemblyCoordinator.setConstructStatusFilter(null);
                    },
                    title: 'Delete constructs?',
                    warnings: [
                      `You're about to delete ${tableConstructs.length} ${pluralize(tableConstructs.length, 'construct')} from the assembly. This action cannot be undone.`,
                    ],
                  });
                }}
                onDeleteRange={async (startIndex: number, endIndex: number) => {
                  const constructCidsToDelete = new Set(
                    tableConstructs.slice(startIndex, endIndex + 1).map(({cid}) => cid)
                  );
                  await withEditConfirmation({
                    confirmationText: 'Delete',
                    onConfirm: () => {
                      assemblyCoordinator.replaceConstructs(
                        assemblyCoordinator.state.constructs.filter(
                          ({cid}) => !constructCidsToDelete.has(cid)
                        )
                      );
                    },
                    title: 'Delete constructs?',
                    warnings: [
                      `You're about to delete ${constructCidsToDelete.size} ${pluralize(constructCidsToDelete.size, 'construct')} from the assembly. This action cannot be undone.`,
                    ],
                  });
                }}
                onReorderConstructs={(reorderedConstructCids) => {
                  const newConstructs = reorderedConstructCids.map(
                    (constructCid) => assemblyCoordinator.getConstructsByCid()[constructCid]
                  );
                  assemblyCoordinator.handleTableConstructsUpdate(
                    newConstructs,
                    getFilteredOutCids(assemblyCoordinator.state.constructs, tableConstructs)
                  );
                }}
                onViewConstructs={async (constructCids: Array<string>) => {
                  analytics.trackEvent(
                    MOLBIO_ANALYTICS_ACTIONS.OPENED_BULK_ASSEMBLY_CONSTRUCT_PREVIEW_MODAL,
                    {
                      actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_OVERVIEW_TAB,
                      assemblyId: assemblyCoordinator.assemblyId,
                    }
                  );
                  // Perf: can update to avoid operating on all constructs up-front
                  const previewConstructs = assemblyCoordinator
                    .getPreviewConstructs()
                    .filter(({cid}) => constructCids.includes(cid));
                  if (previewConstructs.length > 0) {
                    await runModal(ConstructModal, {
                      assemblyId,
                      constructData: previewConstructs.map((construct) => ({
                        ...construct,
                        polymer: assemblyCoordinator.maybeCreatePreviewConstructPolymer(construct.cid),
                      })),
                      initialConstructCid: previewConstructs[0].cid,
                      isReadOnly,
                      bulkAssemblyMethod: assemblyCoordinator.assemblySelector.getMethod().type,
                      canCreatePrimers: assemblyCoordinator.canCreatePrimers(),
                    });
                  }
                }}
                statusFilter={assemblyCoordinator.getConstructStatusFilter()}
                onSetStatusFilter={(newStatusFilter) => {
                  assemblyCoordinator.setConstructStatusFilter(newStatusFilter);
                }}
                autopopulateButtonError={assemblyCoordinator.getAutopopulateButtonStatus()}
                topology={assemblyCoordinator.topology}
              />
            </BenchlingProfiler>
          </div>
        </div>
      </StructuredTableViewPanel>
    </div>
  );
}

const styles = StyleSheet.create({
  fragmentsTableWrapper: {
    marginTop: 12,
    paddingRight: 16,
  },
  constructsTableWrapper: {
    margin: '36px 0',
    paddingRight: 16,
  },
  pageWrapper: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  scrollingContainer: {
    height: '100%',
    width: '100%',
    padding: '10px',
    overflow: 'auto',
  },
});
